import { mtbSoilMoistureLabel } from "./mtb-soil-moisture";

export const MTB_SURFACE_CONDITIONS = [
  "dusty",
  "tacky",
  "loose",
  "damp",
  "muddy",
  "standing-water",
  "snow-or-ice",
] as const;

export type MtbSurfaceCondition = typeof MTB_SURFACE_CONDITIONS[number];

export type MtbReportScope =
  | { kind: "park" }
  | { kind: "zone"; zoneId: string }
  | { kind: "trail"; trailId: string };

export type MtbGroundTruthInput = {
  locationSlug: string;
  surfaceCondition: MtbSurfaceCondition;
  scope: MtbReportScope;
  observedScore: number | null;
  possibleClosure: boolean;
  reviewedStartAt: string | null;
  reviewedEndAt: string | null;
  trackedSessionId: string | null;
};

export type MtbGroundTruthPrefill = {
  trackedSessionId: string;
  reviewedStartAt: string;
  reviewedEndAt: string;
  suggestedTrailIds: string[];
};

export type MtbGroundTruthNormaliseResult =
  | { ok: true; value: MtbGroundTruthInput }
  | { ok: false; error: string };

export type MtbLiveRpcRow = {
  scopeKind: MtbReportScope["kind"];
  zoneId: string | null;
  trailId: string | null;
  surfaceCondition: MtbSurfaceCondition;
  reportCount: number;
  independentRiderCount: number;
  trackedReportCount: number;
  possibleClosureCount: number;
  latestObservedAt: string;
};

export type MtbLiveConditionSummary = {
  condition: MtbSurfaceCondition;
  reportCount: number;
  independentRiderCount: number;
};

export type MtbLiveScopeSummary = {
  scopeKind: MtbReportScope["kind"];
  zoneId: string | null;
  trailId: string | null;
  consensus: MtbSurfaceCondition | null;
  observation: MtbSurfaceCondition | null;
  mixed: boolean;
  reportCount: number;
  independentRiderCount: number;
  trackedReportCount: number;
  possibleClosureCount: number;
  latestObservedAt: string;
  conditions: MtbLiveConditionSummary[];
};

export type MtbLiveSummary = {
  park: MtbLiveScopeSummary | null;
  zones: MtbLiveScopeSummary[];
  trails: MtbLiveScopeSummary[];
};

export const MTB_REPORT_TRAIL_MATCH_MIN_CONFIDENCE = 0.5;
export const MTB_LIVE_REPORT_HOURS = 3;

export function shouldBuildMtbGroundTruthSlot(
  availableSports: readonly string[],
): boolean {
  return availableSports.includes("mtb");
}

const MAX_REVIEW_WINDOW_MS = 12 * 60 * 60 * 1_000;
const VALID_SURFACE_CONDITIONS = new Set<string>(MTB_SURFACE_CONDITIONS);
const ISO_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;

type UnknownRecord = Record<string, unknown>;

export function normaliseMtbGroundTruthInput(
  input: unknown,
): MtbGroundTruthNormaliseResult {
  const inputResult = validateMtbGroundTruthInput(input);
  if (!inputResult.ok) return inputResult;

  const validatedInput = inputResult.value;
  const locationSlug = validatedInput.locationSlug.trim();
  if (!locationSlug) {
    return { ok: false, error: "Choose a place before reporting." };
  }

  const scopeResult = normaliseScope(validatedInput.scope);
  if (!scopeResult.ok) return scopeResult;

  if (
    validatedInput.observedScore !== null
    && (
      !Number.isFinite(validatedInput.observedScore)
      || validatedInput.observedScore < 1
      || validatedInput.observedScore > 10
    )
  ) {
    return { ok: false, error: "Choose a score from 1 to 10." };
  }

  const reviewedWindowResult = normaliseReviewedWindow(
    validatedInput.reviewedStartAt,
    validatedInput.reviewedEndAt,
  );
  if (!reviewedWindowResult.ok) return reviewedWindowResult;

  const trackedSessionId = validatedInput.trackedSessionId?.trim() || null;

  return {
    ok: true,
    value: {
      locationSlug,
      surfaceCondition: validatedInput.surfaceCondition,
      scope: scopeResult.value,
      observedScore: validatedInput.observedScore,
      possibleClosure: validatedInput.possibleClosure,
      reviewedStartAt: reviewedWindowResult.reviewedStartAt,
      reviewedEndAt: reviewedWindowResult.reviewedEndAt,
      trackedSessionId,
    },
  };
}

export function mtbLiveStateKey({
  weightedMoisture,
  precipitationMm,
  soilTemperatureC,
  airTemperatureC,
}: {
  weightedMoisture: number;
  precipitationMm: number;
  soilTemperatureC: number | null;
  airTemperatureC: number;
}): string {
  const label = mtbSoilMoistureLabel(weightedMoisture);
  const rainState = precipitationMm > 0 ? "rain" : "dry";
  const freezeTemperature = soilTemperatureC ?? airTemperatureC;
  const freezeState = freezeTemperature <= 0 ? "frozen" : "thawed";
  return `${label}|${rainState}|${freezeState}`;
}

export function buildMtbLiveSummary(rows: readonly unknown[]): MtbLiveSummary {
  const groups = new Map<string, MtbLiveRpcRow[]>();

  for (const rawRow of rows) {
    const row = normaliseMtbLiveRpcRow(rawRow);
    if (row === null) continue;

    const key = scopeKey(row);
    const group = groups.get(key) ?? [];
    group.push(row);
    groups.set(key, group);
  }

  const summaries = Array.from(groups.values(), summariseScope);

  return {
    park: summaries.find((summary) => summary.scopeKind === "park") ?? null,
    zones: summaries
      .filter((summary) => summary.scopeKind === "zone")
      .sort(compareScopeIds),
    trails: summaries
      .filter((summary) => summary.scopeKind === "trail")
      .sort(compareScopeIds),
  };
}

function validateMtbGroundTruthInput(
  input: unknown,
): MtbGroundTruthNormaliseResult {
  if (!isRecord(input)) {
    return { ok: false, error: "Choose a valid report." };
  }

  if (typeof input.locationSlug !== "string") {
    return { ok: false, error: "Choose a place before reporting." };
  }
  if (!isSurfaceCondition(input.surfaceCondition)) {
    return { ok: false, error: "Choose a valid surface condition." };
  }

  const scopeResult = validateScopeShape(input.scope);
  if (!scopeResult.ok) return scopeResult;

  if (input.observedScore !== null && typeof input.observedScore !== "number") {
    return { ok: false, error: "Choose a score from 1 to 10." };
  }
  if (typeof input.possibleClosure !== "boolean") {
    return { ok: false, error: "Choose a valid closure status." };
  }
  if (!isNullableString(input.reviewedStartAt) || !isNullableString(input.reviewedEndAt)) {
    return { ok: false, error: "Choose a valid review window." };
  }
  if (!isNullableString(input.trackedSessionId)) {
    return { ok: false, error: "Choose a valid tracked session." };
  }

  return {
    ok: true,
    value: {
      locationSlug: input.locationSlug,
      surfaceCondition: input.surfaceCondition,
      scope: scopeResult.value,
      observedScore: input.observedScore,
      possibleClosure: input.possibleClosure,
      reviewedStartAt: input.reviewedStartAt,
      reviewedEndAt: input.reviewedEndAt,
      trackedSessionId: input.trackedSessionId,
    },
  };
}

function validateScopeShape(
  scope: unknown,
): { ok: true; value: MtbReportScope } | { ok: false; error: string } {
  if (!isRecord(scope) || typeof scope.kind !== "string") {
    return { ok: false, error: "Choose a valid report scope." };
  }

  if (scope.kind === "park") {
    if ("zoneId" in scope || "trailId" in scope) {
      return { ok: false, error: "Choose a valid report scope." };
    }
    return { ok: true, value: { kind: "park" } };
  }

  if (scope.kind === "zone") {
    if (typeof scope.zoneId !== "string" || "trailId" in scope) {
      return { ok: false, error: "Choose a valid zone." };
    }
    return { ok: true, value: { kind: "zone", zoneId: scope.zoneId } };
  }

  if (scope.kind === "trail") {
    if (typeof scope.trailId !== "string" || "zoneId" in scope) {
      return { ok: false, error: "Choose a valid trail." };
    }
    return { ok: true, value: { kind: "trail", trailId: scope.trailId } };
  }

  return { ok: false, error: "Choose a valid report scope." };
}

function normaliseScope(
  scope: MtbReportScope,
): { ok: true; value: MtbReportScope } | { ok: false; error: string } {
  if (scope.kind === "park") return { ok: true, value: { kind: "park" } };

  if (scope.kind === "zone") {
    const zoneId = scope.zoneId.trim();
    return zoneId
      ? { ok: true, value: { kind: "zone", zoneId } }
      : { ok: false, error: "Choose a valid zone." };
  }

  if (scope.kind === "trail") {
    const trailId = scope.trailId.trim();
    return trailId
      ? { ok: true, value: { kind: "trail", trailId } }
      : { ok: false, error: "Choose a valid trail." };
  }

  return { ok: false, error: "Choose a valid report scope." };
}

function normaliseReviewedWindow(
  reviewedStartAt: string | null,
  reviewedEndAt: string | null,
):
  | { ok: true; reviewedStartAt: string | null; reviewedEndAt: string | null }
  | { ok: false; error: string } {
  if (reviewedStartAt === null && reviewedEndAt === null) {
    return { ok: true, reviewedStartAt: null, reviewedEndAt: null };
  }
  if (reviewedStartAt === null || reviewedEndAt === null) {
    return { ok: false, error: "Choose a valid review window." };
  }

  const startTime = parseStrictIsoTimestamp(reviewedStartAt);
  const endTime = parseStrictIsoTimestamp(reviewedEndAt);
  if (startTime === null || endTime === null || endTime <= startTime) {
    return { ok: false, error: "Choose a valid review window." };
  }
  if (endTime - startTime > MAX_REVIEW_WINDOW_MS) {
    return { ok: false, error: "Choose a shorter review window." };
  }

  return {
    ok: true,
    reviewedStartAt: new Date(startTime).toISOString(),
    reviewedEndAt: new Date(endTime).toISOString(),
  };
}

function normaliseMtbLiveRpcRow(row: unknown): MtbLiveRpcRow | null {
  if (!isRecord(row)) return null;
  const scopeKind = row.scopeKind;
  if (!isScopeKind(scopeKind)) return null;
  if (!isSurfaceCondition(row.surfaceCondition)) return null;

  const scopeIds = normaliseRpcScopeIds(row, scopeKind);
  if (scopeIds === null) return null;

  if (
    !isPositiveInteger(row.reportCount)
    || !isPositiveInteger(row.independentRiderCount)
    || !isNonNegativeInteger(row.trackedReportCount)
    || !isNonNegativeInteger(row.possibleClosureCount)
  ) {
    return null;
  }
  if (
    row.independentRiderCount > row.reportCount
    || row.trackedReportCount > row.reportCount
    || row.possibleClosureCount > row.reportCount
  ) {
    return null;
  }
  if (typeof row.latestObservedAt !== "string") return null;

  const latestObservedTime = parseStrictIsoTimestamp(row.latestObservedAt);
  if (latestObservedTime === null) return null;

  return {
    scopeKind,
    zoneId: scopeIds.zoneId,
    trailId: scopeIds.trailId,
    surfaceCondition: row.surfaceCondition,
    reportCount: row.reportCount,
    independentRiderCount: row.independentRiderCount,
    trackedReportCount: row.trackedReportCount,
    possibleClosureCount: row.possibleClosureCount,
    latestObservedAt: new Date(latestObservedTime).toISOString(),
  };
}

function normaliseRpcScopeIds(
  row: UnknownRecord,
  scopeKind: MtbReportScope["kind"],
): { zoneId: string | null; trailId: string | null } | null {
  if (scopeKind === "park") {
    return row.zoneId === null && row.trailId === null
      ? { zoneId: null, trailId: null }
      : null;
  }

  if (scopeKind === "zone") {
    if (typeof row.zoneId !== "string" || row.trailId !== null) return null;
    const zoneId = row.zoneId.trim();
    return zoneId ? { zoneId, trailId: null } : null;
  }

  if (typeof row.trailId !== "string" || row.zoneId !== null) return null;
  const trailId = row.trailId.trim();
  return trailId ? { zoneId: null, trailId } : null;
}

function parseStrictIsoTimestamp(value: string): number | null {
  const match = ISO_TIMESTAMP_PATTERN.exec(value);
  if (match === null) return null;

  const [, yearText, monthText, dayText, hourText, minuteText, secondText, , zone] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);

  if (
    month < 1
    || month > 12
    || day < 1
    || day > daysInMonth(year, month)
    || hour > 23
    || minute > 59
    || second > 59
  ) {
    return null;
  }

  if (zone !== "Z") {
    const offsetHour = Number(zone.slice(1, 3));
    const offsetMinute = Number(zone.slice(4, 6));
    if (offsetHour > 23 || offsetMinute > 59) return null;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return isLeapYear ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isScopeKind(value: unknown): value is MtbReportScope["kind"] {
  return value === "park" || value === "zone" || value === "trail";
}

function isSurfaceCondition(value: unknown): value is MtbSurfaceCondition {
  return typeof value === "string" && VALID_SURFACE_CONDITIONS.has(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function scopeKey(row: MtbLiveRpcRow): string {
  if (row.scopeKind === "zone") return `zone:${row.zoneId ?? ""}`;
  if (row.scopeKind === "trail") return `trail:${row.trailId ?? ""}`;
  return "park";
}

function summariseScope(rows: MtbLiveRpcRow[]): MtbLiveScopeSummary {
  const first = rows[0];
  const conditions = new Map<MtbSurfaceCondition, MtbLiveConditionSummary>();
  let reportCount = 0;
  let independentRiderCount = 0;
  let trackedReportCount = 0;
  let possibleClosureCount = 0;
  let latestObservedAt = first.latestObservedAt;

  for (const row of rows) {
    reportCount += row.reportCount;
    independentRiderCount += row.independentRiderCount;
    trackedReportCount += row.trackedReportCount;
    possibleClosureCount += row.possibleClosureCount;
    if (Date.parse(row.latestObservedAt) > Date.parse(latestObservedAt)) {
      latestObservedAt = row.latestObservedAt;
    }

    const condition = conditions.get(row.surfaceCondition) ?? {
      condition: row.surfaceCondition,
      reportCount: 0,
      independentRiderCount: 0,
    };
    condition.reportCount += row.reportCount;
    condition.independentRiderCount += row.independentRiderCount;
    conditions.set(row.surfaceCondition, condition);
  }

  const conditionList = Array.from(conditions.values()).sort(
    (left, right) => surfaceConditionIndex(left.condition) - surfaceConditionIndex(right.condition),
  );
  const onlyCondition = conditionList.length === 1 ? conditionList[0] : null;
  const consensus = onlyCondition && onlyCondition.independentRiderCount >= 2
    ? onlyCondition.condition
    : null;

  return {
    scopeKind: first.scopeKind,
    zoneId: first.scopeKind === "zone" ? first.zoneId : null,
    trailId: first.scopeKind === "trail" ? first.trailId : null,
    consensus,
    observation: onlyCondition && consensus === null ? onlyCondition.condition : null,
    mixed: conditionList.length > 1,
    reportCount,
    independentRiderCount,
    trackedReportCount,
    possibleClosureCount,
    latestObservedAt,
    conditions: conditionList,
  };
}

function surfaceConditionIndex(condition: MtbSurfaceCondition): number {
  return MTB_SURFACE_CONDITIONS.indexOf(condition);
}

function compareScopeIds(left: MtbLiveScopeSummary, right: MtbLiveScopeSummary): number {
  const leftId = left.zoneId ?? left.trailId ?? "";
  const rightId = right.zoneId ?? right.trailId ?? "";
  return leftId.localeCompare(rightId);
}
